import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Heart, HeartOff, Mail, Link as LinkIcon, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANIM_STYLE = `
@keyframes partyDoor {
  0%   { transform: perspective(400px) rotateY(0deg); }
  22%  { transform: perspective(400px) rotateY(-65deg); }
  42%  { transform: perspective(400px) rotateY(0deg); }
  62%  { transform: perspective(400px) rotateY(-65deg); }
  78%  { transform: perspective(400px) rotateY(0deg); }
  92%  { transform: perspective(400px) rotateY(-65deg); }
  100% { transform: perspective(400px) rotateY(-65deg); }
}
@keyframes partyLight {
  0%,21%  { opacity: 0; }
  22%,41% { opacity: 1; }
  42%,61% { opacity: 0; }
  62%,77% { opacity: 1; }
  78%,91% { opacity: 0; }
  92%,100%{ opacity: 1; }
}
`;

if (typeof document !== 'undefined' && !document.getElementById('party-anims-matches')) {
  const s = document.createElement('style');
  s.id = 'party-anims-matches';
  s.textContent = ANIM_STYLE;
  document.head.appendChild(s);
}

function DoorIcon() {
  return (
    <span style={{ position: 'relative', width: 18, height: 18, flexShrink: 0, display: 'inline-flex', marginRight: 4 }}>
      {/* luz de fondo */}
      <span style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: '#fef3c7', borderRadius: '3px 3px 0 0',
        animation: 'partyLight 4.5s ease-in-out 0.5s infinite',
      }} />
      {/* panel puerta */}
      <span style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: '#d4a574', borderRadius: '3px 3px 0 0',
        transformOrigin: 'left center',
        animation: 'partyDoor 4.5s ease-in-out 0.5s infinite',
        overflow: 'hidden',
      }}>
        <span style={{
          position: 'absolute', right: 3, top: '52%', width: 2.5, height: 2.5,
          background: '#b8860b', borderRadius: '50%', transform: 'translateY(-50%)',
        }} />
      </span>
    </span>
  );
}

export default function Matches() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ matches: [], pending: [], sent: [] });
  const [confirmingRemove, setConfirmingRemove] = useState(null);
  const [newMessageUsers, setNewMessageUsers] = useState({});

  const getAuthHeaders = () => ({
    withCredentials: true,
    headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) }
  });

  const checkNewMessages = useCallback(async (matches) => {
    if (!matches || matches.length === 0) return;
    const updated = {};
    for (const match of matches) {
      try {
        const res = await axios.get(`${API}/messages/${match.user_id}`, getAuthHeaders());
        const messages = res.data.messages || [];
        if (messages.length === 0) continue;
        const lastMsg = messages[messages.length - 1];
        if (lastMsg.sender_id === match.user_id) {
          const key = `lastReadMsg_${match.user_id}`;
          const lastRead = localStorage.getItem(key);
          if (lastRead !== lastMsg.created_at) {
            updated[match.user_id] = true;
          }
        }
      } catch (e) {}
    }
    setNewMessageUsers(updated);
  }, []);

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/matches`, getAuthHeaders());
      setData(response.data);
      checkNewMessages(response.data.matches);
    } catch (error) {
      console.error('Error fetching matches:', error);
      toast.error('Error al cargar los contactos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMatches(); }, []);

  const handleShowInterest = async (userId) => {
    try {
      const response = await axios.post(`${API}/interests`, { target_user_id: userId }, getAuthHeaders());
      if (response.data.is_match) {
        toast.success('¡Es un match! Ya podéis ver vuestros contactos');
      } else {
        toast.success('Interés mostrado');
      }
      fetchMatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al mostrar interés');
    }
  };

  const handleRemoveInterest = async (userId) => {
    try {
      await axios.delete(`${API}/interests/${userId}`, getAuthHeaders());
      toast.success('Interés eliminado');
      fetchMatches();
    } catch (error) {
      toast.error('Error al eliminar interés');
    }
  };

  const handleRemoveMatch = async (userId) => {
    try {
      await axios.delete(`${API}/matches/${userId}`, getAuthHeaders());
      setConfirmingRemove(null);
      fetchMatches();
    } catch (error) {
      toast.error('Error al quitar match');
      setConfirmingRemove(null);
    }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return 'https://images.unsplash.com/photo-1760551937527-2bc6cfe45180?w=400';
    if (photo.startsWith('http')) return photo;
    return `${API}/files/${photo}`;
  };

  const UserCard = ({ userProfile, type, hasNewMessage }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-4 flex gap-4 relative" data-testid={`match-card-${userProfile.user_id}`}>
      {hasNewMessage && <span className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full" />}
      <Link to={`/profiles/${userProfile.user_id}`}>
        <img src={getPhotoUrl(userProfile.profile_photo)} alt={userProfile.name} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/profiles/${userProfile.user_id}`}>
          <h3 className="font-heading font-semibold text-gray-900 hover:text-[hsl(144,18%,45%)] transition-colors">
            {userProfile.name}{userProfile.age ? `, ${userProfile.age}` : ''}
          </h3>
        </Link>
        {userProfile.city && <p className="text-sm text-gray-500">{userProfile.city}</p>}

        {type === 'match' && (
          <div className="mt-2 space-y-1">
            {(userProfile.contact_email || userProfile.email) && (
              <a href={`mailto:${userProfile.contact_email || userProfile.email}`} className="flex items-center gap-1 text-sm text-[hsl(144,18%,45%)] hover:text-[hsl(144,18%,38%)]">
                <Mail className="w-4 h-4" />
                {userProfile.contact_email || userProfile.email}
              </a>
            )}
            {userProfile.contact_link && (
              <a href={userProfile.contact_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-[hsl(144,18%,45%)] hover:text-[hsl(144,18%,38%)]">
                <LinkIcon className="w-4 h-4" />
                Perfil externo
              </a>
            )}
          </div>
        )}

        <div className="mt-3 flex gap-2 flex-wrap">
          {type === 'pending' && (
            <>
              <Button
                size="sm"
                onClick={() => handleShowInterest(userProfile.user_id)}
                className="bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-lg flex items-center"
                data-testid={`accept-interest-${userProfile.user_id}`}
              >
                <DoorIcon />
                Aceptar
              </Button>
              <Link to={`/profiles/${userProfile.user_id}`}><Button size="sm" variant="outline" className="rounded-lg">Ver perfil</Button></Link>
            </>
          )}
          {type === 'sent' && (
            <>
              <Button size="sm" variant="outline" onClick={() => handleRemoveInterest(userProfile.user_id)} className="rounded-lg text-red-500 hover:text-red-600 hover:border-red-200" data-testid={`remove-interest-${userProfile.user_id}`}>
                <HeartOff className="w-4 h-4 mr-1" />Cancelar
              </Button>
              <Link to={`/profiles/${userProfile.user_id}`}><Button size="sm" variant="outline" className="rounded-lg">Ver perfil</Button></Link>
            </>
          )}
          {type === 'match' && (
            <>
              <Link to={`/chat/${userProfile.user_id}`}>
                <Button size="sm" className="bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-lg" data-testid={`chat-btn-${userProfile.user_id}`}>
                  <MessageCircle className="w-4 h-4 mr-1" />Chat
                </Button>
              </Link>
              <Link to={`/profiles/${userProfile.user_id}`}><Button size="sm" variant="outline" className="rounded-lg">Ver perfil</Button></Link>
              {confirmingRemove === userProfile.user_id ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">¿Seguro?</span>
                  <button onClick={() => handleRemoveMatch(userProfile.user_id)} className="text-xs px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600" data-testid={`confirm-remove-match-${userProfile.user_id}`}>Sí, quitar</button>
                  <button onClick={() => setConfirmingRemove(null)} className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300" data-testid={`cancel-remove-match-${userProfile.user_id}`}>No</button>
                </div>
              ) : (
                <button onClick={() => setConfirmingRemove(userProfile.user_id)} className="text-xs px-2 py-1 text-red-400 hover:text-red-600 rounded-lg" data-testid={`remove-match-${userProfile.user_id}`}>Quitar match</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  if (!user?.profile_completed) {
    return (
      <div className="min-h-screen bg-[hsl(35,25%,91%)]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h2 className="font-heading font-bold text-2xl text-gray-900 mb-4">Completa tu perfil</h2>
            <p className="text-gray-600 mb-8">Para ver tus <span translate="no">matches</span>, primero necesitas completar tu perfil.</p>
            <Link to="/onboarding"><Button className="bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-full">Completar perfil</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(35,25%,91%)]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-heading font-bold text-2xl text-gray-900 mb-6">Mis contactos</h1>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[hsl(144,18%,45%)]" />
          </div>
        ) : (
          <Tabs defaultValue="matches" className="w-full">
            <TabsList className="w-full grid grid-cols-3 mb-6">
              <TabsTrigger value="matches" className="relative" data-testid="matches-tab">
                <span translate="no">Matches</span>
                {data.matches.length > 0 && (
                  <span className="ml-2 bg-white text-[hsl(144,18%,35%)] text-xs px-2 py-0.5 rounded-full shadow-sm border border-[hsl(144,18%,80%)]">{data.matches.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="pending" className="relative" data-testid="pending-tab">
                Recibidos
                {data.pending.length > 0 && (
                  <span className="ml-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">{data.pending.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="sent" data-testid="sent-tab">
                Enviados
                {data.sent.length > 0 && (
                  <span className="ml-2 bg-white text-gray-500 text-xs px-2 py-0.5 rounded-full shadow-sm border border-gray-200">{data.sent.length}</span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="matches">
              {data.matches.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-[hsl(35,20%,88%)]">
                  <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600">Aún no tienes <span translate="no">matches</span></p>
                  <p className="text-sm text-gray-500 mt-2">Cuando tú y otra persona mostréis interés mutuo, aparecerán aquí</p>
                  <Link to="/feed"><Button className="mt-4 bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-full">Explorar perfiles</Button></Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.matches.map(profile => <UserCard key={profile.user_id} userProfile={profile} type="match" hasNewMessage={newMessageUsers[profile.user_id]} />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="pending">
              {data.pending.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-[hsl(35,20%,88%)]">
                  <p className="text-gray-600">No tienes solicitudes pendientes</p>
                  <p className="text-sm text-gray-500 mt-2">Cuando alguien muestre interés en ti, aparecerá aquí</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.pending.map(profile => <UserCard key={profile.user_id} userProfile={profile} type="pending" />)}
                </div>
              )}
            </TabsContent>

            <TabsContent value="sent">
              {data.sent.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-[hsl(35,20%,88%)]">
                  <p className="text-gray-600">No has mostrado interés en nadie</p>
                  <Link to="/feed"><Button className="mt-4 bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-full">Explorar perfiles</Button></Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {data.sent.map(profile => <UserCard key={profile.user_id} userProfile={profile} type="sent" />)}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
